import { useCallback, useState, useMemo } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { Switch } from "@mui/material";
import { Paper, Text } from "$components";
import useOrder from "$hooks/useOrder";
import useJStyles from "$hooks/useJStyles";
import SuborderFieldArray from "./SubordersTable";
import ConformityRenderer from "../../ConformityRenderer";

type Props = {
  reception: any;
  isReadOnly?: boolean;
  control: any;
  register: any;
  getValues: any;
  setValue: any;
  errors: any;
  watch: any;
};

function ProductsSection({
  reception,
  control,
  register,
  setValue,
  getValues,
  watch,
  errors,
  isReadOnly = false,
}: Props) {
  const { fields } = useFieldArray({
    control,
    name: "orders",
  });
  const [totalUnconform, setTotalUnconform] = useState(0);
  const [filteredStates, setFilteredStates] = useState<Record<number, boolean>>(
    {}
  );
  const { classes } = useOrder();
  const { jclasses } = useJStyles();

  // Use useWatch instead of watch for better performance
  const orders = useWatch({
    control,
    name: "orders",
    defaultValue: [],
  });

  // Watch for productQuantity data
  const productQuantity = useWatch({
    control,
    name: "productQuantity",
    defaultValue: [],
  });

  // Pre-compute order references in a memoized function
  const orderReferences = useMemo(
    () => orders.map((order) => order.reference),
    [orders]
  );

  const createToggleFiltered = useCallback((orderIndex: number) => {
    return () => {
      setFilteredStates((prev) => ({
        ...prev,
        [orderIndex]: !prev[orderIndex],
      }));
    };
  }, []);

  // Helper function to calculate product index - extracted to avoid ESLint loop function error
  const calculateProductIndex = useCallback(
    (
      targetOrder: number,
      targetSuborder: number,
      targetProduct: number,
      ordersData: any[]
    ): number => {
      let calculatedIndex = 0;

      // Count all products before this one
      for (let orderIdx = 0; orderIdx < targetOrder; orderIdx++) {
        const order = ordersData[orderIdx];
        if (order?.suborders) {
          for (let subIdx = 0; subIdx < order.suborders.length; subIdx++) {
            const suborder = order.suborders[subIdx];
            if (suborder?.suborderProducts) {
              calculatedIndex += suborder.suborderProducts.length;
            }
          }
        }
      }

      // Add products from current order before current suborder
      for (let suborderIdx = 0; suborderIdx < targetSuborder; suborderIdx++) {
        const suborder = ordersData[targetOrder]?.suborders?.[suborderIdx];
        if (suborder?.suborderProducts) {
          calculatedIndex += suborder.suborderProducts.length;
        }
      }

      // Add current product index
      calculatedIndex += targetProduct;

      return calculatedIndex;
    },
    []
  );

  // Memoize the update order conformity function
  const updateOrderConformity = useCallback(
    (
      orderIndex: number,
      suborderIndex: number,
      productIndex: number,
      value: boolean
    ) => {
      setValue(
        `productQuantity[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${productIndex}]`,
        value,
        { shouldDirty: true }
      );
    },
    [setValue]
  );

  // Create a memoized lookup map for product quantity indices for faster lookups
  const productQuantityMap = useMemo(() => {
    const map = new Map();

    productQuantity.forEach((pq: any, index: number) => {
      const key = `${pq.orderReference}-${pq.suborderReference}-${pq.productId}`;
      map.set(key, index);
    });

    return map;
  }, [productQuantity]);

  // Optimize the lookup function using the map
  const currentProductQuantityIndex = useCallback(
    (iOrder: number, iSuborder: number, iProduct: number): number => {
      const orderRef = orderReferences[iOrder];
      const suborderRef = orders[iOrder]?.suborders?.[iSuborder]?.reference;
      const productRef =
        orders[iOrder]?.suborders?.[iSuborder]?.suborderProducts?.[iProduct]
          ?.reference;

      const key = `${orderRef}-${suborderRef}-${productRef}`;
      const index = productQuantityMap.get(key) ?? -1;

      // EMERGENCY FIX: If index is -1, create a proper array index
      if (index === -1) {
        const calculatedIndex = calculateProductIndex(
          iOrder,
          iSuborder,
          iProduct,
          orders
        );

        // Initialize this product in the form if it doesn't exist
        const currentProductQuantity = getValues("productQuantity") || [];
        if (!currentProductQuantity[calculatedIndex]) {
          setValue(`productQuantity[${calculatedIndex}]`, {
            orderReference: orderRef,
            suborderReference: suborderRef,
            productId: productRef,
            conformity: true,
            alreadyReceivedQuantity: 0,
            receivedQuantity: 0,
            unconformQuantity: 0,
          });
        }

        return calculatedIndex;
      }

      return index;
    },
    [
      orderReferences,
      orders,
      productQuantityMap,
      setValue,
      getValues,
      calculateProductIndex,
    ]
  );

  // Optimize the product lookup function using the map
  const currentProductQuantityIndexInOrder = useCallback(
    (iOrder: number, iSuborder: number, product: any): number => {
      const orderRef = orderReferences[iOrder];
      const suborderRef = orders[iOrder]?.suborders?.[iSuborder]?.reference;
      const productRef = product.reference;

      const key = `${orderRef}-${suborderRef}-${productRef}`;
      return productQuantityMap.get(key) ?? -1;
    },
    [orderReferences, orders, productQuantityMap]
  );

  // Show message if no fields
  const noFields = fields?.length === 0;

  return (
    <>
      <div className={jclasses.formSectionHead}>
        <Text type="h3">Produits</Text>
        {noFields ? (
          <Text type="text">Aucune commande de renseignée</Text>
        ) : (
          <ConformityRenderer totalUnconform={totalUnconform} />
        )}
      </div>

      {fields.map((item: any, index: number) => {
        // Pre-compute values needed for rendering
        const orderReference = orders[index]?.reference;
        const isFiltered = filteredStates[index] || false;
        const toggleFiltered = createToggleFiltered(index);

        return (
          <Paper key={item.id} className={classes.paperForm}>
            <div className={classes.paperFormHead}>
              <Text type="text">
                {"N° de commande "}
                {orderReference}
              </Text>
            </div>
            <div className={classes.paperFormBody}>
              <div className={classes.formFilter}>
                <Switch
                  checked={isFiltered}
                  onChange={toggleFiltered}
                  name={`showCompany-${index}`}
                  color="primary"
                />
                <Text type="text">Afficher les sous-commandes</Text>
              </div>
              <div style={{ marginTop: 15, marginBottom: 15 }}>
                <SuborderFieldArray
                  isReadOnly={isReadOnly}
                  nestIndex={index}
                  {...{
                    control,
                    register,
                    getValues,
                    setValue,
                    watch,
                    errors,
                    reception,
                    updateOrderConformity,
                    currentProductQuantityIndex,
                    currentProductQuantityIndexInOrder,
                    totalUnconform,
                    setTotalUnconform,
                    filtered: isFiltered,
                  }}
                />
              </div>
            </div>
          </Paper>
        );
      })}
    </>
  );
}

export default ProductsSection;
